import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';

interface UserMaster {
  user_id: string;
  password: string;
}

// Generate random salt
const generateSalt = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
};

// Hash password with salt using PBKDF2
const hashPassword = async (password: string, salt: string): Promise<string> => {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: encoder.encode(salt), iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    256
  );
  const hashArray = Array.from(new Uint8Array(bits));
  return salt + ':' + hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const AdminLoginPage: React.FC = () => {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [errors, setErrors] = useState<{ user?: string; pass?: string; cred?: string }>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!user.trim()) errs.user = 'Username is required.';
    if (!pass.trim()) errs.pass = 'Password is required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setErrors({});

    try {

      // First get the user, then verify password
      const { data: users, error } = await supabase
        .from('user_master')
        .select('user_id, password')
        .eq('user_id', user);

      if (error || !users || users.length === 0) {
        setErrors({ cred: 'Invalid username or password.' });
        setPass('');
        return;
      }

      // Verify the hashed password matches (stored as salt:hash)
      const userData = users[0];
      const [salt] = userData.password.split(':');
      const hashedPass = await hashPassword(pass, salt);
      if (hashedPass === userData.password) {
        sessionStorage.setItem('adminAuth', '1');
        sessionStorage.setItem('adminUser', user);
        navigate('/admin');
      } else {
        setErrors({ cred: 'Invalid username or password.' });
        setPass('');
      }
    } catch (err) {
      console.error('Login error:', err);
      setErrors({ cred: 'Invalid username or password.' });
      setPass('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'linear-gradient(140deg, #1e293b 0%, #0f172a 100%)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 5%' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '20px', padding: '44px 40px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '1.15rem', marginBottom: '8px', justifyContent: 'center' }}>
            <div className="logo-mark">360</div>
            <span><span style={{ color: '#93c5fd' }}>Digi</span><span style={{ color: '#fdba74' }}>Learning</span></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd', fontSize: '0.76rem', fontWeight: 700, padding: '4px 12px', borderRadius: '50px' }}>🔒 Admin Portal</div>
          </div>
        </div>

        <h2 style={{ color: '#fff', fontSize: '1.3rem', textAlign: 'center', marginBottom: '5px' }}>Admin Login</h2>
        <p style={{ color: '#94a3b8', fontSize: '0.86rem', textAlign: 'center', marginBottom: '26px' }}>Secure access to submission management and analytics</p>

        <form onSubmit={doLogin}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', marginBottom: '5px' }}>Admin Username</label>
            <input style={{ width: '100%', padding: '11px 14px', background: '#0f172a', border: `1.5px solid ${errors.user ? '#f87171' : '#334155'}`, borderRadius: '8px', fontSize: '0.9rem', fontFamily: "'Nunito',sans-serif", color: '#fff', outline: 'none' }} type="text" placeholder="admin" value={user} onChange={e => setUser(e.target.value)} autoComplete="username" />
            {errors.user && <div style={{ fontSize: '0.78rem', color: '#f87171', marginTop: '4px', fontWeight: 600 }}>{errors.user}</div>}
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', marginBottom: '5px' }}>Password</label>
            <input style={{ width: '100%', padding: '11px 14px', background: '#0f172a', border: `1.5px solid ${errors.pass ? '#f87171' : '#334155'}`, borderRadius: '8px', fontSize: '0.9rem', fontFamily: "'Nunito',sans-serif", color: '#fff', outline: 'none' }} type="password" placeholder="Enter admin password" value={pass} onChange={e => setPass(e.target.value)} autoComplete="current-password" />
            {errors.pass && <div style={{ fontSize: '0.78rem', color: '#f87171', marginTop: '4px', fontWeight: 600 }}>{errors.pass}</div>}
          </div>
          {errors.cred && <div style={{ fontSize: '0.78rem', color: '#f87171', marginBottom: '10px', textAlign: 'center', fontWeight: 600 }}>{errors.cred}</div>}
          <button type="submit" className="btn btn-blue btn-full btn-lg" style={{ marginTop: '6px' }} disabled={loading}>{loading ? 'Verifying...' : 'Access Admin Dashboard →'}</button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '16px' }}>
          <Link to="/" style={{ color: '#475569', fontSize: '0.8rem' }}>← Back to main site</Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
